import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { GpuJsendEnvelope } from './gpuServerResponse';
import { isAbortError, readJsendBody } from './gpuServerResponse';
import { budgetReadErrorMessage } from './gpuBudgetResponse';

export interface GpuBudgetReadState<T> {
  data: T | null;
  /** True during the first load AND a background `refetch` (which keeps `data` on screen). */
  loading: boolean;
  /** The last read's failure; `data` keeps the last good value. */
  error: string | null;
  /** Re-read in the background: the current `data` stays until the answer arrives. */
  refetch: () => Promise<void>;
  /** Replace `data` locally (e.g. with what a write returned), aborting a read in flight. */
  setData: Dispatch<SetStateAction<T | null>>;
}

/**
 * Fetch a budget route once (when `enabled` and authenticated), keeping `pick(body)` of a JSend
 * success; `pick` returns `null` for a success missing its payload, reported as an error. Pass a
 * module-level `pick` so its identity is stable.
 *
 * Budget routes are database-only, but they are still never polled: the read runs once, then only on
 * `refetch`. `apiFetch` is read through a ref, so an OIDC silent renew (a new `apiFetch` identity)
 * never re-runs it; the unmount cleanup re-arms the guard, so StrictMode's development remount
 * reads again.
 */
export function useGpuBudgetFetchOnce<T>(
  path: string,
  pick: (body: GpuJsendEnvelope) => T | null,
  fallback: string,
  enabled: boolean,
): GpuBudgetReadState<T> {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  useEffect(() => {
    apiFetchRef.current = apiFetch;
  }, [apiFetch]);

  const [data, setDataState] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const hasFetchedRef = useRef(false);

  const fetchData = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetchRef.current(path, { signal: controller.signal });
      const body = await readJsendBody<GpuJsendEnvelope>(response);
      if (controller.signal.aborted) {
        return;
      }
      const picked = body?.status === 'success' ? pick(body) : null;
      if (picked !== null) {
        setDataState(picked);
        return;
      }
      setError(budgetReadErrorMessage(body, fallback));
    } catch (err) {
      if (isAbortError(err, controller.signal)) {
        return;
      }
      setError(err instanceof Error ? err.message : fallback);
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setLoading(false);
      }
    }
  }, [path, pick, fallback]);

  const setData = useCallback<Dispatch<SetStateAction<T | null>>>((value) => {
    // A read that started before the write could answer with the older state: drop it.
    if (abortRef.current !== null) {
      abortRef.current.abort();
      abortRef.current = null;
      setLoading(false);
    }
    setDataState(value);
  }, []);

  useEffect(() => {
    if (enabled && auth.isAuthenticated && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      void fetchData();
    }
  }, [enabled, auth.isAuthenticated, fetchData]);

  // Forgetting the fetch makes a remount read again — StrictMode's development remount included,
  // whose cleanup aborts the first read (without the reset, nothing would ever re-issue it).
  useEffect(
    () => () => {
      abortRef.current?.abort();
      hasFetchedRef.current = false;
    },
    [],
  );

  return { data, loading, error, refetch: fetchData, setData };
}
